/**
 * @file LoginCredentialFields.tsx
 * @description Поля входа по макету Commora (phase19.29): «Электронная
 * почта» со значком конверта и «Пароль» со значком замка и кнопкой
 * «показать/скрыть».
 *
 * Значки слева — декоративные (aria-hidden), текст поля отступает от них.
 * Кнопка-«глаз» переключает type password ↔ text, подписана для программ
 * чтения с экрана («Показать пароль» / «Скрыть пароль», aria-pressed), цель
 * касания — высота поля (48px до 1024, 44 с 1024). autocomplete
 * (username / current-password) сохранён — менеджеры паролей работают.
 */

import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useState } from "react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface FieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** Значок внутри поля слева. */
const ICON_CLASS =
  "pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-on-surface-variant";

export function LoginEmailField({ value, onChange, disabled }: FieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="login-email">Электронная почта</Label>
      <div className="relative">
        <Mail className={ICON_CLASS} aria-hidden />
        <Input
          id="login-email"
          type="email"
          autoComplete="username"
          inputMode="email"
          required
          placeholder="user@company.ru"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="pl-11"
        />
      </div>
    </div>
  );
}

export function LoginPasswordField({ value, onChange, disabled }: FieldProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="login-password">Пароль</Label>
      <div className="relative">
        <Lock className={ICON_CLASS} aria-hidden />
        <Input
          id="login-password"
          type={visible ? "text" : "password"}
          autoComplete="current-password"
          required
          minLength={1}
          placeholder="Введите пароль"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          // Справа место под «глаз», чтобы значок не перекрывал текст.
          className="pl-11 pr-12"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Скрыть пароль" : "Показать пароль"}
          aria-pressed={visible}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-lg text-on-surface-variant outline-none hover:text-on-surface focus-visible:ring-2 focus-visible:ring-ring"
        >
          {visible ? <EyeOff className="size-5" aria-hidden /> : <Eye className="size-5" aria-hidden />}
        </button>
      </div>
    </div>
  );
}
